/**
 * EChartBase Component
 *
 * Core wrapper for Apache ECharts with:
 * - Auto-resize on container change
 * - Built-in loading spinner
 * - Empty state handling
 * - Optional toolbox (export, zoom)
 *
 * @component
 */

import React, { useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import ReactECharts from 'echarts-for-react';
import { Box, Typography } from '@mui/material';
import { CustomLoader } from '../ui';

const EChartBase = forwardRef(({
  option,
  height = 400,
  loading = false,
  showToolbox = false,
  toolbox = {},
  emptyMessage = 'No data available',
  onEvents = {},
  style = {},
  sx = {},
  notMerge = false,
  lazyUpdate = true,
}, ref) => {
  const chartRef = useRef(null);

  // Expose chart instance to parent via ref
  useImperativeHandle(ref, () => ({
    getEchartsInstance: () => chartRef.current?.getEchartsInstance?.(),
  }));

  const toolboxConfig = showToolbox
    ? {
        show: true,
        right: 5,
        top: -5,
        orient: 'vertical',
        itemSize: 13,
        itemGap: 6,
        feature: {
          ...(toolbox.saveAsImage !== false && {
            saveAsImage: { title: 'Save', pixelRatio: 2 },
          }),
          ...(toolbox.dataZoom && {
            dataZoom: { title: { zoom: 'Zoom', back: 'Reset' } },
          }),
          ...(toolbox.restore && {
            restore: { title: 'Restore' },
          }),
        },
      }
    : { show: false };

  const mergedOption = { ...option, toolbox: toolboxConfig };

  useEffect(() => {
    const handleResize = () => {
      if (chartRef.current) {
        chartRef.current.getEchartsInstance()?.resize();
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (loading) {
    return (
      <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.50', borderRadius: 2, ...sx }}>
        <CustomLoader size={80} captionSize="0.9rem" />
      </Box>
    );
  }

  const hasData = option?.series?.some((s) => s.data && s.data.length > 0);
  if (!option || !hasData) {
    return (
      <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', ...sx }}>
        <Typography color="text.secondary">{emptyMessage}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', ...sx }}>
      <ReactECharts
        ref={chartRef}
        option={mergedOption}
        style={{ height, width: '100%', ...style }}
        notMerge={notMerge}
        lazyUpdate={lazyUpdate}
        onEvents={onEvents}
        opts={{ renderer: 'canvas' }}
      />
    </Box>
  );
});

EChartBase.displayName = 'EChartBase';

EChartBase.propTypes = {
  option: PropTypes.object,
  height: PropTypes.number,
  loading: PropTypes.bool,
  showToolbox: PropTypes.bool,
  toolbox: PropTypes.object,
  emptyMessage: PropTypes.string,
  onEvents: PropTypes.object,
  style: PropTypes.object,
  sx: PropTypes.object,
  notMerge: PropTypes.bool,
  lazyUpdate: PropTypes.bool,
};

export default EChartBase;

